import type { MfaMethodSummary, SessionState } from '../api/types.js';

/**
 * In-memory login-flow state held on `<sso-app>`. This is NOT the session.
 *
 * It caches what the server already told us this navigation (e.g. the 2FA methods returned by the
 * password step) so the choose-method page need not refetch. It is intentionally non-persistent: on
 * reload the SPA re-asks the server "where am I?" via GET /api/session, keeping the server authoritative.
 */
export class FlowContext {
  /** Existing 2FA methods the password step returned (for the choose-method page). */
  methods: MfaMethodSummary[] = [];

  /** One-time recovery codes captured at enrollment, consumed once by the recovery-codes page. */
  recoveryCodes: string[] = [];

  /** Last session snapshot from GET /api/session. */
  session?: SessionState;

  /**
   * Same-origin return target captured at login (the `?return_uri=` of an app-initiated authorize hop).
   * Held here so it survives the client-side navigations of the 2FA / enrollment steps, which drop the
   * query string — otherwise the flow would forget where to go and fall back to the launcher.
   */
  returnUri?: string;

  /** Cache the methods returned by the password step. */
  setMethods(methods: MfaMethodSummary[]): void {
    this.methods = methods;
  }

  /** Stash the (already-validated) same-origin return target for the rest of the flow. */
  setReturnUri(returnUri: string | null | undefined): void {
    this.returnUri = returnUri ?? undefined;
  }

  /** Stash one-time recovery codes for the recovery-codes page to show. */
  setRecoveryCodes(codes: string[]): void {
    this.recoveryCodes = codes;
  }

  /** Read and clear the one-time recovery codes. */
  takeRecoveryCodes(): string[] {
    const codes = this.recoveryCodes;
    this.recoveryCodes = [];
    return codes;
  }

  /** Hydrate from a server session snapshot. */
  hydrate(session: SessionState): void {
    this.session = session;
  }

  /** Clear transient state (e.g. on logout). */
  reset(): void {
    this.methods = [];
    this.recoveryCodes = [];
    this.session = undefined;
    this.returnUri = undefined;
  }
}
